import ConsultationRichInput from '@brands/Components/Inputs/RichTextField/RichTextField';
import { OralHealthReportSubmit } from '@brands/Dashboard/ConsultationPage/assets/types';
import NotesSection from '@brands/Dashboard/ConsultationPage/CollapsibleSections/CaseNotesSection/CaseNotesSection';
import EducationSection from '@brands/Dashboard/ConsultationPage/CollapsibleSections/EducationSection/EducationSection';
import FollowUpSection from '@brands/Dashboard/ConsultationPage/CollapsibleSections/FollowUpSection/FollowUpSection';
import PrescriptionSection from '@brands/Dashboard/ConsultationPage/CollapsibleSections/PrescriptionSection/PrescriptionSection';
import { ICase } from '@brands/services/cases/types/ICase';
import React, { SetStateAction } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import { UserProfile } from '../../../../services/identity/types/UserProfile';
import { updateOneReport } from '../../../../services/oneReport/updateOneReport';
import { checkCollapsibleHeaderColor } from '../../../../Utils/getCollapsibleHeader';
import styles from '../../styles.module.scss';

type PlanSectionProps = {
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<SetStateAction<ICase>>;
  userInfo: UserProfile;
  register: UseFormRegister<OralHealthReportSubmit>;
  setValue: UseFormSetValue<OralHealthReportSubmit>;
  trigger: UseFormTrigger<OralHealthReportSubmit>;
  errors: FieldErrors<OralHealthReportSubmit>;
  journeyPaused?: boolean;
  journeyAtLimit?: boolean;
};

const PlanSection = ({
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
  userInfo,
  register,
  setValue,
  trigger,
  errors,
  journeyPaused = false,
  journeyAtLimit = false,
}: PlanSectionProps): JSX.Element => {
  const dispositionError = typeof errors.plan?.disposition?.message === 'string' ? errors.plan.disposition.message : '';

  const hasDispositionError = Boolean(dispositionError);

  const isEditable =
    checkCollapsibleHeaderColor(thisCase || null, userInfo, 'Assessment') === 'edit' ||
    checkCollapsibleHeaderColor(thisCase || null, userInfo, 'Assessment') === 'editNotRequired';

  const saveOneReportPlan = async (attributeName: string, attributeValue: any): Promise<void> => {
    if (attributeValue || attributeValue === '') {
      const updateData = {
        [attributeName]: attributeValue,
      };
      const response = await updateOneReport(thisCase?.one_report.id || '', updateData, userInfo.role.name);

      setCase({
        ...thisCase,
        one_report: {
          ...thisCase.one_report,
          ...response,
        },
      });
    }
  };

  return (
    <div className={styles.subjectiveDiv}>
      <span className={styles.subjectiveTitle}>Plan</span>
      <div className={styles.subjective}>
        <span className={`fs-unmask ${styles.subjectiveSpan}`}>
          Disposition/Intervention or recommendations provided
          <span className={`${styles.cDTDivSpanRequired} fs-unmask`}>*</span>
        </span>
        <ConsultationRichInput
          value={consultationForm.plan?.disposition || ''}
          editable={isEditable}
          onChange={(html) => {
            setConsultationForm((prev) => ({
              ...prev,
              plan: {
                ...prev.plan,
                disposition: html,
              },
            }));

            setValue('plan.disposition', html, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
          }}
          onBlurSave={async (html) => saveOneReportPlan('disposition', html)}
          registerRef={(ref) => register('plan.disposition').ref(ref)}
          error={hasDispositionError}
          className={`fs-exclude ${styles.subjectiveInput}`}
          placeholder="Add disposition or recommendations here"
          style={{ color: hasDispositionError ? '#BA271A' : '' }}
        />
        {hasDispositionError && <div className={styles.sectionErrorText}>{dispositionError}</div>}
      </div>
      <div className={styles.possibleTreatmentNeedsSectionDiv}>
        <PrescriptionSection
          consultationForm={consultationForm}
          setConsultationForm={setConsultationForm}
          thisCase={thisCase}
          setCase={setCase}
          userInfo={userInfo}
          register={register}
          setValue={setValue}
          trigger={trigger}
        />
        <NotesSection
          thisCase={thisCase}
          setCase={setCase}
          setConsultationForm={setConsultationForm}
          consultationForm={consultationForm}
          type="CASE_NOTE"
        />
        <NotesSection
          thisCase={thisCase}
          setCase={setCase}
          setConsultationForm={setConsultationForm}
          consultationForm={consultationForm}
          type="INTERNAL_CASE_NOTE"
        />
        <EducationSection
          consultationForm={consultationForm}
          setConsultationForm={setConsultationForm}
          thisCase={thisCase}
          setCase={setCase}
          userInfo={userInfo}
          register={register}
          setValue={setValue}
          trigger={trigger}
          errors={errors}
        />
        <FollowUpSection
          consultationForm={consultationForm}
          setConsultationForm={setConsultationForm}
          thisCase={thisCase}
          setCase={setCase}
          userInfo={userInfo}
          errors={errors}
          journeyPaused={journeyPaused}
          journeyAtLimit={journeyAtLimit}
        />
      </div>
    </div>
  );
};

export default React.memo(PlanSection);
